import { useCallback, useEffect, useRef, useState } from 'react';
import * as singleSpa from 'single-spa';
import { History } from 'history5';
import { useLocation } from 'react-router-dom';

import { history5 } from '@futrli/shared-services';

interface SbcCoreSceneUiApi {
  configure: (history: History) => void;
  refetch: () => void;
}

export const useGlobalNav = (show: boolean, config?: typeof window.__sbc_config) => {
  const [isBootstrapped, setIsBootstrapped] = useState(singleSpa.getAppNames().includes('sbc.core.globalnav.ui'));
  const bootstrapStarted = useRef(false);
  const lastBidRef = useRef('');
  const location = useLocation();
  const sceneRef = useRef<SbcCoreSceneUiApi>();

  // Use a ref and update it with the show value so we dont get stale references in MFE activity functions
  const showRef = useRef(show);
  showRef.current = show;

  // Grab the business Id from the URL
  const bid = new URLSearchParams(location.search).get('bid') || '';

  /**
   * Load and configure everything ready for global nav launch
   */
  const bootstrap = useCallback(async () => {
    bootstrapStarted.current = true;
    const [scene] = await Promise.all([System.import<SbcCoreSceneUiApi>('@sage/sbc.core.scene.ui')]);

    // Now we configure scene and give it our history object
    scene.configure(history5);
    sceneRef.current = scene;

    // Next we register the global nav MFE
    singleSpa.registerApplication({
      name: 'sbc.core.globalnav.ui',
      app: async () =>
        (await System.import<Record<string, singleSpa.LifeCycles>>('sbc.core.globalnav.ui')).NavBarLifecycles,
      activeWhen: [
        () => {
          return showRef.current;
        }
      ],
      customProps: {
        history: history5,
        domElementGetter: () => {
          return document.getElementById('sage-global-nav');
        }
      }
    });

    // Next we register the consent MFE
    singleSpa.registerApplication({
      name: 'sbc.core.consent.ui',
      app: () => System.import<singleSpa.LifeCycles>('sbc.core.consent.ui'),
      activeWhen: ['/'], // no activity function here as we dont want to keep reloading this app
      customProps: {
        history: history5,
        domElementGetter: () => {
          return document.getElementById('sage-consent');
        }
      }
    });

    setIsBootstrapped(true);
    singleSpa.start();
  }, []);

  // Bootstrap / start MFEs depending upon state
  useEffect(() => {
    if (show && config && !isBootstrapped && !bootstrapStarted.current) {
      bootstrap();
    }
    if (isBootstrapped) {
      // If business ID (bid) in URL has changed, force a refresh of scene data.
      // This updates the businessId stored in scene which makes the global nav reload its data
      if (bid && bid !== lastBidRef.current) {
        lastBidRef.current = bid;
        sceneRef.current?.refetch();
      }
      singleSpa.triggerAppChange(); // Force apps to check if they should be active
    }
  }, [bootstrap, isBootstrapped, config, show, bid]);
};
